import React, { useState, useContext } from "react";
import axios from "axios";
import { Row, Col } from "react-bootstrap";
import { Form, Field } from "react-final-form";
import { useMutation } from "@apollo/client";

import { ReactSelectAdapter } from "../../reuseableComponents/forms/ReactSelectAdapter";
import Label from "../../reuseableComponents/label";
import ButtonSubmit from "../../reuseableComponents/buttons/ButtonSubmit";
import ButtonProcessing from "../../reuseableComponents/buttons/ButtonProcessing";
import { UPLOAD_FILE } from "../../../queries/archiveQrys/secureQrys/fileUploadQrys";
import FileUploadSuccess from "./FileUploadSuccess";
import FileUploadSuccessLSL from "./FileUploadSuccessLSL";
import ErrorHandler from "../../reuseableComponents/ErrorHandler";
import { appConfig } from "../../../configuration/AppConfig";
import { EnvironmentContext } from "../../../configuration/EnvironmentContext";
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';

const style = {
  control: (base) => ({
    ...base,
    border: "1px solid grey",
    // This line disable the blue border
    boxShadow: "none"
  }),
  placeholder: (base) => ({ ...base, color: "#595959" })
};

async function uploadFileToS3(env, fileName, file) {
  try {
    let response = await axios.post(
      `${appConfig.s3FileUpload}?fileName=${fileName}&bucketName=${appConfig.bucketName}`,
      file,
      {
        headers: {
          "Content-Type": `multipart/form-data; bounday=${file._boundary}`
        }
      }
    );

    if (response.status === 200) {
      return { status: 200 };
    } else {
      return {
        status: 400,
        message: "An error occured while uploading your file."
      };
    }
  } catch (err) {
    console.log("ERROR: ", err);
  }
}

async function getPresignedUrl(env, fileName, file, metadata) {
  let response;
  if (metadata.ddwFileType === 298) {
    let systemName = metadata.system.label.substring(0, metadata.system.label.indexOf("/"));
    systemName = encodeURIComponent(systemName);
    response = await axios.get(
      `${appConfig.s3PutPresignedUrl}?system_id=${metadata.systemId}&file_description=${metadata.fileDescription}&ddw_file_type=${metadata.ddwFileType}` +
        `&file_type=${metadata.fileType}&file_size=${metadata.fileSize}&email=${metadata.createdBy}&gis=${metadata.gis}` +
        `&lsl_cert=${metadata.lslCert}&bucketName=${appConfig.bucketName}&fileName=${fileName}&env=${env}&system_name=${systemName}&utah_id=${metadata.utahId}`
    );
  } else {
    const systemName = metadata.system.label.substring(0, metadata.system.label.indexOf("/"));
    response = await axios.get(
      `${appConfig.s3PutPresignedUrl}?system_id=${metadata.systemId}&file_type=${metadata.fileType}` +
        `&bucketName=${appConfig.bucketName}&fileName=${fileName}&env=${env}&system_name=${systemName}&utah_id=${metadata.utahId}`
    );
  }

  if (response.status === 200) {
    let res = await axios.put(response.data.url, file);
    console.log("PRESIGNED URL RESPONSE: ", res);

    if (res.status === 200) {
      return { status: 200 };
    } else return { status: res.status, message: "An error occured while uploading your file." };
  }
}

const FileUploadForm = ({ menus, user }) => {
  const { env } = useContext(EnvironmentContext);
  const [file, setFile] = useState();
  const [invalidChar, setInvalidChar] = useState();
  const [largeFile, setLargeFile] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [lslSubmitted, setLslSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [isFileError, setIsFileError] = useState(false);
  const [fileErrorMessage, setFileErrorMessage] = useState(null);
  const [uploadFileMetadata, { error }] = useMutation(UPLOAD_FILE);

  const onFileChange = (evt) => {
    setFile(evt.target.files);
  };

  const handleShow = () => {
    setIsFileError(true);
  };

  const handleClose = () => {
    setIsFileError(false);
    setInvalidChar(false);
  }

  //https://codeburst.io/react-js-api-calls-to-aws-lambda-api-gateway-and-dealing-with-cors-89fb897eb04d
  async function onSubmit(data) {
    const acceptableChars = /^[a-zA-Z0-9\s\-_.]*$/;
    
    if (!acceptableChars.test(file[0].name)){
      setFileErrorMessage(`${file[0].name} Your file name contains unacceptable special characters.`);
      setInvalidChar(true);
      handleShow();
      return;
    }

    if (file[0].name.split('.').pop().toLowerCase() !== 'xlsx' && data.ddwFileTypeObj.value === 298) {
      setFileErrorMessage(`${file[0].name} Upload was unsuccessful. Only workbooks with with file extension .xlsx are allowed for upload. Please check your workbooks file extension and make sure it's the most recent version of the workbook. If you have questions or need additional help, please email us at leadinventory@utah.gov.`);
      handleShow();
      return;
    }
    
    setUploading(true);
    let docType = data.ddwFileTypeObj.label.includes("&")
      ? data.ddwFileTypeObj.label.replace("&", "and")
      : data.ddwFileTypeObj.label;

    let filePath;
    let utahId = data.system.label.split("/")[1];

    // Lead Service Line files have a special location
    if (data.ddwFileTypeObj.value === 298) {
      filePath = `lead-service-line/to-validate/${utahId}/${file[0].name
        .split(" ")
        .join("_")}`;
    } else {
      filePath = `pws-system-files/${utahId}/${docType.split(" ").join("-")}/${file[0].name
        .split(" ")
        .join("_")}`;
    }

    /**File size in measured in bytes. Files larger then (5*1024*1024) => (5MB) should use presigned URL upload to avoid timeout.
     */

    const fileUploadVariables = {
      ...data,
      systemId: data.system.value,
      fileName: file[0].name.split(" ").join("_"),
      fileType: data.ddwFileTypeObj.label,
      fileSize: file[0].size,
      pws_id: utahId,
      ddwFileType: data.ddwFileTypeObj.value,
      filePath: filePath,
      gis: data.gis ? data.gis.value : null,
      lslCert: data.lslCert,
      isSystemRep: data.isSystemRep,
      utahId: utahId,
      env
    };
    
    let upload;
    if (file[0].size >= 5 * 1024 * 1024 || data.ddwFileTypeObj.value === 298) {
      //use presigned url
      setLargeFile(true);
      upload = await getPresignedUrl(env, filePath, file[0], fileUploadVariables);
    } else {
      upload = await uploadFileToS3(env, filePath, file[0]);
    }

    if (upload.status === 200) {
      // if Lead Service Line Inventory, skip inserting to sys_file_upload
      if (data.ddwFileTypeObj.value === 298) {
        setLslSubmitted(true);
        setUploading(false);
        setLargeFile(false);
      } else {
        uploadFileMetadata({
          variables: fileUploadVariables,
          onCompleted: (data) => {
            setSubmitted(true);
            setUploading(false);
            setLargeFile(false);
          },
          onError: (err) => {
            setUploading(false);
            setLargeFile(false);
            setErrorMsg(err);
          }
        });
      }
    } else {
      setErrorMsg(upload.message);
      setUploading(false);
      setLargeFile(false);
    }
  }

  if (submitted) {
    return <FileUploadSuccess />;
  }

  if (lslSubmitted) {
    return <FileUploadSuccessLSL />;
  }

  if (error) {
    setUploading(false);
    return <ErrorHandler error={error} />;
  }

  return (
    <>
      <Row>
        <Row>
        <Col>
            <Modal
              show={isFileError}
              onHide={handleClose}
              backdrop="static"
              keyboard={false}
              size="lg"
              centered
            >
              <Modal.Header closeButton>
                <Modal.Title>File Name Error</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {fileErrorMessage}
                {invalidChar && <p>File names must only contain alphanumerics, periods, hypens, or underscores.<br/> Please rename your file and try again.</p>}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="primary" onClick={handleClose}>
                  Close
                </Button>
              </Modal.Footer>
            </Modal>
        </Col>
        </Row>
        <Col>
          <Form
            onSubmit={onSubmit}
            initialValues={{
              createdBy: user.email
            }}
            keepDirtyOnReinitialize
            render={({ handleSubmit, form, submitting, values }) => (
              <form onSubmit={handleSubmit}>
                <Row>
                  <Col lg={6}>
                    <Label htmlFor='county-list' label='County' required />
                    {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
                    <Field autoFocus
                      name='counties'
                      inputId='county-list'
                      component={ReactSelectAdapter}
                      options={menus.getCounties}
                      className='mb-4'
                      styles={style}
                    />
                  </Col>
                </Row>
                {values.counties ? (
                  <>
                    <Row>
                      <Col>
                        <Label htmlFor='water-systems' label='Water System' required />
                        <Field
                          name='system'
                          inputId='water-systems'
                          component={ReactSelectAdapter}
                          options={menus.getWaterSystemsAndCounties.filter(
                            (systems) => systems.countyId === values.counties.value
                          )}
                          className='mb-4'
                          required
                        />
                      </Col>
                      <Col>
                        <Label htmlFor='file-type-list' label='File Type' required />
                        <Field
                          name='ddwFileTypeObj'
                          inputId='file-type-list'
                          component={ReactSelectAdapter}
                          options={menus.getFileUploadFileTypes}
                          className='mb-4'
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <Label htmlFor='file-purpose' label='File Purpose' />
                        <Field
                          name='fileDescription'
                          id='file-purpose'
                          component='textarea'
                          className='mb-4 p-2 w-100'
                          style={{ borderRadius: "5px", borderColor: "grey" }}
                        />
                      </Col>
                    </Row>

                    <Row>
                      {values.ddwFileTypeObj && values.ddwFileTypeObj.value === 298 ? (
                        <Col lg={6}>
                          <Label htmlFor='gis-requirement' label='LCRR Public Accessibility Requirement' />

                          <br />
                          <span>
                            Would you use the DDW GIS Map (Available in Summer 2024) to meet the LCRR public
                            accessibility requirement?
                          </span>
                          <br />
                          <span>Note: If you are a water system larger than 50,000, click n/a.</span>
                          <Field
                            name='gis'
                            inputId='gis-requirement'
                            component={ReactSelectAdapter}
                            options={[
                              { value: false, label: "N/A" },
                              { value: false, label: "No" },
                              { value: true, label: "Yes" }
                            ]}
                            className='mb-4 w-25'
                          />
                        </Col>
                      ) : (
                        <Col></Col>
                      )}
                    </Row>
                    
                    <Row>
                      <Col lg={4}>
                        <Label htmlFor='file-upload' label='File Upload' required />
                        <input id='file-upload' className='form-control mb-4' type='file' onChange={onFileChange} required aria-required="true" />
                      </Col>
                      {values.ddwFileTypeObj && values.ddwFileTypeObj.value === 298 ? (
                        <Col className='m-auto'>
                          <span>
                            If you would like to submit supplemental documentation, please email it to
                            leadinventory@utah.gov
                          </span>
                        </Col>
                      ) : (
                        <Col></Col>
                      )}
                    </Row>
                    {values.ddwFileTypeObj && values.ddwFileTypeObj.value === 298 ? (
                      <Row>
                        <Col>
                          <label className='mt-3' required>
                            <Field
                              name='lslCert'
                              component='input'
                              className='big-checkbox mt-0 me-3'
                              type='checkbox'
                              required
                            />
                            The public water system submitting this inventory template hereby certifies that a service
                            line inventory of the entire service area, including both the system-owned side and
                            customer-owned side service lines, has been completed and verified following review of
                            previous materials evaluations and all available records, maps, and information on permits,
                            codes, and/or inspections of all service line material at the time of submittal. <br />{" "}
                            <br />
                            The public water system certifies that all service lines have been determined through an
                            evidence-based record, method, or technique to be "Non-Lead", "Lead", "Galvanized Requiring
                            Replacement," or "Lead Status Unknown" service lines as defined below:
                            <ul>
                              <li>"Lead" means where all or a portion of the service line is lead.</li>
                              <li>
                                "Galvanized Requiring Replacement" means a galvanized service line that is known to have
                                been downstream of a lead service line in the past or is unknown to have been downstream
                                of a lead service line currently or in the past.
                              </li>
                              <li>
                                "Lead Status Unknown" means a service line material that is not known to be lead,
                                galvanized requiring replacement, or non-lead." - We need the name of who is submitting
                                the inventory and their contact information.”{" "}
                              </li>
                            </ul>
                          </label>
                        </Col>
                      </Row>
                    ) : (
                      <Row>
                        <Col>
                          <label className='mt-3 d-flex align-items-center'>
                            <Field
                              name='isSystemRep'
                              component='input'
                              className='big-checkbox me-3'
                              type='checkbox'
                              required
                            />
                            I certify that I represent this system
                          </label>
                        </Col>
                      </Row>
                    )}
                    <Row>
                      <Col>
                        {!uploading && (!values.ddwFileTypeObj || !values.system || !file) ? (
                          <ButtonSubmit id='gray-primary' disabled />
                        ) : !uploading && (values.ddwFileTypeObj || values.system || file) ? (
                          <ButtonSubmit id='ddw-primary' />
                        ) : (
                          <ButtonProcessing id='ddw-primary' disabled />
                        )}
                      </Col>

                      <Col>
                        <span className='float-end'>
                          <p><strong>Submitted By:</strong></p>
                          <span>{user.email}</span>
                          <br />
                          <span>{new Date().toDateString()}</span>
                        </span>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        {largeFile ? <span>Uploading a large file. This may take a moment.</span> : ""}
                        <span className='error' role="alert">{errorMsg}</span>
                      </Col>
                    </Row>
                  </>
                ) : (
                  ""
                )}
              </form>
            )}
          />
        </Col>
      </Row>
    </>
  );
};
export default FileUploadForm;
